// The declaration of the untyped geocoding module must follow this file into every program that compiles it
// (the apps include only their own declarations): a `.d.ts` without exports cannot be imported instead.
// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- see above
/// <reference path="./mapbox-sdk-geocoding.d.ts" />
/**
 * Mapbox libraries are heavy (~1.8MB) and must be kept out of the initial bundle.
 *
 * These helpers load each library lazily through a dynamic `import()` (so esbuild
 * emits them as async chunks that no longer block the application startup) and
 * memoize the successful result so a given library is only fetched once for the
 * whole app. A failed load is NOT cached: the memoized promise is cleared on
 * rejection so the next call retries the import (e.g. after a transient network
 * error), instead of leaving maps permanently broken until a full reload.
 */

/** The `mapbox-gl` module namespace (`Map`, `Marker`, `Popup`, controls, ...). */
export type MapboxGlModule = typeof import('mapbox-gl')
/** The `MapboxDraw` constructor (default export of `@mapbox/mapbox-gl-draw`). */
export type MapboxDrawConstructor = typeof import('@mapbox/mapbox-gl-draw')
/** The geocoding service factory (default export of `@mapbox/mapbox-sdk`). */
export type MapboxGeocodingFactory = typeof import('@mapbox/mapbox-sdk/services/geocoding').default

let mapboxGlPromise: Promise<MapboxGlModule> | undefined
let mapboxDrawPromise: Promise<MapboxDrawConstructor> | undefined
let mapboxGeocodingPromise: Promise<MapboxGeocodingFactory> | undefined

export function loadMapboxGl(): Promise<MapboxGlModule> {
  if (!mapboxGlPromise) {
    mapboxGlPromise = import('mapbox-gl')
      .then((module) => {
        // mapbox-gl v3 ships a UMD bundle: depending on the bundler's CommonJS/ESM
        // interop, the real namespace (`Map`, `Marker`, controls, ...) lives on the
        // module's `default` export rather than on the module record itself. Fall back
        // to `default` when the top-level `Map` constructor is not directly available,
        // otherwise `new mapboxgl.Map()` throws "Map is not a constructor".
        const candidate = module as MapboxGlModule & { default?: MapboxGlModule }
        return candidate.Map ? candidate : (candidate.default as MapboxGlModule)
      })
      .catch((error) => {
        mapboxGlPromise = undefined
        throw error
      })
  }
  return mapboxGlPromise
}

export function loadMapboxDraw(): Promise<MapboxDrawConstructor> {
  if (!mapboxDrawPromise) {
    mapboxDrawPromise = import('@mapbox/mapbox-gl-draw')
      .then((module) => module.default)
      .catch((error) => {
        mapboxDrawPromise = undefined
        throw error
      })
  }
  return mapboxDrawPromise
}

export function loadMapboxGeocoding(): Promise<MapboxGeocodingFactory> {
  if (!mapboxGeocodingPromise) {
    mapboxGeocodingPromise = import('@mapbox/mapbox-sdk/services/geocoding')
      .then((module) => module.default)
      .catch((error) => {
        mapboxGeocodingPromise = undefined
        throw error
      })
  }
  return mapboxGeocodingPromise
}
